/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align */

import {ProjectElement}          from './project.element';
import {ProjectElementType}      from './project.element.type';
import { BlobType }              from '../blob';

import { Project }               from '../project';

import { Logger }                from '@/util/logger';

import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {PropertiesHolder} from "@/models/properties.holder";
import {Observable} from "rxjs";
import {first} from "rxjs/operators";
import {StringLineViewerMetadata} from "@/screens/ide/string.line.viewer.metadata";
import {BatchView} from "@/screens/ide/batchviewer/batch.view";
import {BatchViewerMetadata} from "@/screens/ide/batch.viewer.metadata";
import {SimpleSimJob} from "@/models/sim/simple.sim.job";
import {SimulationJob, SimulationJobType} from "@/models/sim/simulation.job";
import {empty, errorAsText, fromMySQLDate} from "@/util/util";
import {LineSimJobElement} from "@/models/project/line.sim.job.element";
import {ProjectElementFactory} from "@/models/project.element.factory";
import {TaskUpdate} from "@/models/task.update";
import {TaskBatchSim} from "@/models/task.batch.sim";
import {TaskBatchSimFailed} from "@/models/task.batch.sim.failed";

import {v4 as uuidv4} from 'uuid';
import {BatchSimJob} from "@/models/sim/batch.sim.job";
import {BatchSimJobElement} from "@/models/project/batch.sim.job.element";
import {SimConfig} from "@/models/sim/sim.config";
import {JSONBrowserMetadata} from "@/screens/ide/json.browser.metadata";
import {WindowType} from "@/screens/ide/window";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";

/**
 *
 * @class CorridorBatchElement
 *
 * See also notes on BatchGroupElement.
 *
 * This node corresponds 1:1 with batches in the Scalable Work Engine; each corridor gets its own
 * batch.  These batches then roll up into a "batch of batches"...the parent node in the tree is
 * a BatchGroupElement.
 *
 * The main special consideration here is that the data isn't directly tied to these nodes, each
 * one has a batch id, which is the database ID/OID of the actual scalable work engine batch. So
 * operations like viewing the batch, or deleting the batch etc.  Must all be done via the SWE
 * and the batch id.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      Feburary 2022
 *
 */
@ProjectElementFactoryDecorator('CorridorBatchElement')
export class CorridorBatchElement extends ProjectElement {

  public batchActionKeys: string[] = [
    'simcorridor',
    'simandmonitor',
    'kpiview',
    'kpidownload',
    'editcorridor',
    'renamecorridor',
    'deletecorridor',
    'viewinbrowser'
  ];

  /** the unique (UUID style) key for this batch group */
  public _key: string              = '';

  /** the path to the folder for the batch of batches */
  public _prefix: string           = '';

  /** the SWE ID/OID of the actual batch */
  public _batch: string            = '';

  /** the error when creating the corridor batch (if there was one */
  public _creationError: string    = '';

  /** standard constructor */
  constructor(
    project: Project,
         id: string,
     parent: ProjectElement,
      label: string  = 'Corridor Batch',
        key: string  = '',
     prefix: string  = '',
      batch: string  = '',
      error: string  = '',
    publish: boolean = false
  ) {

    super(project, id, ProjectElementType.CorridorBatch);

    this._acceptsBlobs.push(BlobType.None);

    this._enabled       = true;
    this._label         = label;
    this._key           = key;
    this._prefix        = prefix;
    this._batch         = batch;
    this._creationError = error;
    this._icon          = 'fa fa-th';
    this._elementKind   = 'Corridor Batch';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring CorridorBatchElement");
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return !this.isBusy();
  }

  /** helper to check if at least one result has a KPI rollup */
  public hasKPI(): boolean {

    /* check if at least one of the results has a KPI rollup. */

    let found: boolean = false;

    this.children().forEach((item) => {

      const result: BatchSimJobElement = item as BatchSimJobElement;

      if (!empty(result._job.kpi)) {

        found = true;
      }

    });

    return found;
  }

  /** fetch the path to the rolled up KPI for this corridor */
  public kpiBlobPath(): string {

    /*
     * we return the aggregate KPI rollup for all cells in the result.  If there is
     * more than one result, we just pick one..for now, use the last child...its likely
     * the most recent result.
     *
     */

    let key: string = '';

    this.children().forEach((item) => {

      const result: BatchSimJobElement = item as BatchSimJobElement;

      if (!empty(result._job.kpi)) {

        key = result._job.kpi;
      }

    });

    return key;
  }

  /**
   * some elements might have longer running or asynchronous tasks that need to be done as part of
   * deletion.  To allow them to do <whatever>; similar to secondaryBlobs(), we ask nodes if they
   * have deletion tasks, and if they do, we just merge these in with the observables we're
   * creating anyway to do things like clean up blobs tied to elements.  Then we we forkJoin()
   * the whole merged result.
   */
  public deletionTasks(): Observable<any>[] {

    /*
     * deletion of a corridor batch means deletion of the SWE side batch; which we have the batch
     * id for.  So when there is a deletion of this element, we pass the deletion worker an
     * observable that will delete the batch on the SWE side.  This will then get run like all
     * the other deletion tasks.
     *
     */

    Logger.debug(`[CorridorBatchElement] auto-delete of batch (${this._label}): `, this._batch);

    return [
      this._project.services.swe.deleteBatch(this._batch).pipe(first())
    ];
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.simcorridor = {
      name:     'Simulate',
      icon:     'fas fa-play',
      disabled:  this.isBusy()
    }

    actionSet.simandmonitor = {
      name:     'Simulate + Monitor',
      icon:     'fas fa-play',
      disabled:  this.isBusy()
    }

    actionSet.editcorridor = {
      name:     'Edit',
      icon:     'fas fa-edit',
      disabled:  this.isBusy()
    }

    actionSet.kpiview = {
      name: 'KPI View',
      icon: 'fas fa-keyboard',
      disabled: !this.hasKPI()
    };

    actionSet.kpidownload = {
      name: 'KPI (Download)',
      icon: 'fas fa-file-download',
      disabled: !this.hasKPI()
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renamecorridor = {
      name:     'Rename',
      icon:     'fas fa-tag',
      disabled:  this.isBusy()
    }

    actionSet.deletecorridor = {
      name:     'Delete',
      icon:     'fas fa-trash-alt',
      disabled:  this.isBusy()
    };

    return actionSet;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      key:          this._key,
      prefix:       this._prefix,
      batch:        this._batch,
      create_error: this._creationError
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    this._key           = node.key;
    this._prefix        = node.prefix;
    this._batch         = node.batch;
    this._creationError = node.create_error;
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Key',     this._key);
    properties.set('Folder',  this._prefix);
    properties.set('Batch #', this._batch);
    properties.set('Error',   this._creationError);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.batchActionKeys.find(x => x === key);
  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    /* do any dimension specific stuff */

    switch (key) {

      case 'editcorridor': {

        Logger.debug(`[CorridorBatchElement] ediiting batch ${this._batch}: `, this._label);

        this._project.services.ide.launchWindow(
          new BatchViewerMetadata(
            '',
            '',
            this._batch,
            this._label,
            this._prefix,
            BatchView.EDIT
          )
        );


        return;
      }

      case 'simcorridor': {

        this.spawn();

        return;
      }

      case 'simandmonitor': {

        this.spawn(true);

        return;
      }

      case 'renamecorridor': {

        /* use a dialog to get the new name and then re-key any children... */

        const dim: CorridorBatchElement = this;

        dim._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            Logger.debug('[CorridorBatchElement] validating: ', name);

            /* is it a valid name? */

            if (name.trim() === '') {

              reject('Batch name can not be empty.');

              return;
            }

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = dim._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Batch of that name already exists.');

                return;
              }

            }

            resolve(name);

          },
          (name) => {

            /*
             * do the actual renaming...the dimension doesn't have a blob key, but child
             * blob keys might be dependent on the dimension name
             *
             */

            /* figure out what the path changes are ... */

            const parents: ProjectElement[] = this.ancestors().reverse();
            parents.shift();
            const search: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${this._label}`;

            const replace: string = dim._project.dirname() +
              '/' +
              parents.map(x => x._label).join('/') + `/${name}`;

            Logger.debug(`[CorridorBatchElement] renaming... ${search} => ${replace}...`);

            /* adjust (in bulk)... */

            this.shiftBlobs(search, replace, (result) => {

              if (result !== true) {

                /* there was a problem! */

                Logger.error(`[CorridorBatchElement] could not properly rename children: `, result);

                return;
              }

              Logger.debug('[CorridorBatchElement] children re-named, saving...');

              dim._label = name;

              dim.redraw();

              dim.save();

            });

          }, {
            title: 'Rename Batch',
            placeholder: this._label
          });

        return;
      }

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this.kpiBlobPath(), this.kpiBlobPath()));

        break;
      }

      case 'deletecorridor': {

        Logger.debug('[CorridorBatchElement] deleting batch: ', this._label);

        this.deleteElements(selection);

        return;
      }

      case 'kpiview': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            ` KPI: ${this._label}`,
            this.kpiBlobPath()
          )
        );

        return ;
      }

      case 'kpidownload': {

        this.dowloadBlob(BlobType.Json, this.kpiBlobPath(), 'latest', this._label + '-kpi.json');

        return ;
      }

    }

  }

  /** launch a new result for this batch */
  public spawn(monitor: boolean = false) {

    Logger.debug(`[CorridorBatchElement] spawn - from ... ${this._batch}: `, this._label);

    const parent: CorridorBatchElement = this;
    let   resultName: string           = 'Result ';
    const task: string                 = TaskUpdate.generateId();

    const date: Date = new Date();

    resultName += date.getFullYear() + '-' + date.getMonth()   + '-' + date.getDay() + ' ';
    resultName += date.getHours()    + ':' + date.getMinutes() + ':' + date.getSeconds();

    parent.busy();

    /* start up a long-running task to track the progress for spawning and running the result ... */

    let update = new TaskBatchSim(task, `Spawning result for ${parent._label}: ...(please wait)...`, 0, null);

    Logger.debug(`[CorridorBatchElement] progress: ${parent._id} `, update);

    this._project.services.taskMonitor.progress(update);

    parent._project.services.swe.spawn(parent._batch, resultName).pipe(first())
      .subscribe(
        (resultDef: any) => {

          Logger.debug(`[CorridorBatchElement] spawn - new result: `, resultDef);

          let update = new TaskBatchSim(task, `${parent._label}: empty result created, launching...`, 0, null);

          Logger.debug(`[CorridorBatchElement] progress: ${parent._id} `, update);

          parent._project.services.taskMonitor.progress(update);

          /* actually start the job running in Cloud Run ... */

          parent._project.services.swe.trigger(resultDef.id.toString()).pipe(first())
            .subscribe(
              (triggerResult: any) => {

                Logger.debug(`[CorridorBatchElement] LAUNCHED: ${resultDef._id}`);

                parent._project.services.taskMonitor.progress(new TaskBatchSim(task, `${parent._label}: Launched!`, 0, null));

                Logger.debug(`[CorridorBatchElement] progress: ${parent._id} `, update);

                /* create the result node and its job ... */

                const job: BatchSimJob = new BatchSimJob({
                  type: SimulationJobType.BATCH_SIM,
                  jobId: resultDef.id,
                  jobUuid: uuidv4(),
                  status: SimulationJob.statusType(resultDef.status),
                  created: fromMySQLDate(resultDef.created_at),
                  dequeue: fromMySQLDate(resultDef.created_at),
                  updated: fromMySQLDate(resultDef.updated_at),
                  batchId: resultDef.batch_id.toString(),
                  resultId: resultDef.id.toString(),
                  config: new SimConfig({})
                });

                const el: BatchSimJobElement = ProjectElementFactory.fromObject(
                  ProjectElementType.BatchSimResult,
                  {
                    project: parent._project,
                    id: '',
                    job,
                    parent,
                    task,
                    publish: true
                  }) as BatchSimJobElement;

                el._project.expand([el._parent]);

                el.save();

                if (monitor) {

                  /*
                   * now that we have a result and a job, if they requested monitoring as well, pop
                   * a monitoring panel for this result.
                   *
                   */

                  Logger.debug(`[CorridorBatchElement] popping LIVE monitor for result (${resultDef.id})`);

                  this._project.services.ide.launchWindow(
                    new BatchViewerMetadata(
                      '',
                      '',
                      job.resultId,
                      `#${job.id()} Result`,
                      '',
                      BatchView.MONITOR
                    )
                  );
                }

              },
              (error) => {

                Logger.debug(`[CorridorBatchElement] could not trigger result run: `, error);

                const msg = errorAsText(error);

                parent._project.services.taskMonitor.progress(new TaskBatchSimFailed(task,`${parent._label}: can't run batch: ${msg}`));

              });
        },
        (error) => {

          Logger.debug(`[CorridorBatchElement] could not spawn result for batch: `, error);

          const msg = errorAsText(error);

          parent._project.services.taskMonitor.progress(new TaskBatchSimFailed(task,`${parent._label}: can't spawn: ${msg}`));

        });

  }
}
